import React, { useEffect, useRef, useState } from 'react';
import { webLoadingFalse, webNavigate } from '../../services/navigate.service';
import CloseIcon from '../../images/CloseIcon';
import EditIconV2 from '../../images/EditIconV2';
import DeleteIconV2 from '../../images/DeleteIconV2';
import TagStatus from '../../components/TagStatus';
import { getListSupplierInventory } from '../../services/supplier.service';
import { deleteSupplierReturn } from '../../services/supplierReturn.service';
import { showDeleteToast } from '../../services/toast.service';
import { showApiError } from '../../services/apiError';
import { useResponsive } from "../../hook/useResponsive";
import {
    ListColumn,
    ListQuery,
    ListRowAction,
    ListScreen,
    ListScreenHandle,
    TEXT,
    cells,
    formatUpdatedAt,
    t,
} from '../../components/list';
interface InforTable {
    title: string;
    path: string;
    desc: string;
    titleCreate: string;
}
interface SupplierReturnRow {
    id: string;
    returnNumber: string;
    supplier: { companyName: string } | null;
    location: { name: string } | null;
    order: { internalNumber: string } | null;
    dispatchedAt: Date | null;
    totalGross: number | null;
    updatedAt: Date;
    statusId: number;
}
const LIST_STATUS = [
    { id: 1, name: 'Draft', value: 1 },
    { id: 2, name: 'Dispatched', value: 2 },
    { id: 3, name: 'Credited', value: 3 },
    { id: 4, name: 'Cancelled', value: 4 },
];
/** Plain text cell for the "-" placeholders the old table showed for empty fields. */
const plain = (value: any) => (
    <div
        className="font-inter"
        style={{ fontSize: '14px', color: TEXT, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
    >
        {value}
    </div>
);
const SupplierReturn = () => {
    const { isMobile } = useResponsive();
    const preLanguage = localStorage.getItem('language');
    const [language, setLanguage] = useState<any>(null);
    const searchInputRef = useRef<HTMLInputElement | null>(null);
    const listRef = useRef<ListScreenHandle>(null);
    const [showDelete, setShowDelete] = useState<boolean>(false);
    const [idDelete, setIdDelete] = useState<string>('');
    const getStatus = (statusValue: number) =>
        LIST_STATUS.find((v) => v.value === statusValue)?.name ?? '/';
    const inforTable: InforTable = {
        title:
            language && language?.['No supplier return yet'] ? language?.['No supplier return yet'] : 'No supplier return yet',
        path: '/supplierReturn/new',
        desc:
            language &&
                language?.['Create a supplier return to continue']
                ? language?.[
                'Create a supplier return to continue'
                ]
                : 'Create a supplier return to continue',
        titleCreate: language && language?.['New Role'] ? language?.['New Role'] : 'New Role',
    };

    const handleKeyDown = (event: KeyboardEvent) => {
        if ((event.ctrlKey || event.metaKey) && event.key === 'f') {
            event.preventDefault();
            if (searchInputRef.current) {
                searchInputRef.current.focus();
            }
        }
    };

    useEffect(() => {
        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, []);
    useEffect(() => {
        if (window.languageContext) {
            setLanguage(window.languageContext.language);
        }
    }, [window, window.languageContext]);

    /**
     * One page of supplier returns. The endpoint has no unified search (only
     * per-column searchReturnNumber/... params, which the old UI never sent),
     * so the kit renders no search box.
     */
    const fetchReturns = async (query: ListQuery) => {
        webLoadingFalse();
        const response: any = await getListSupplierInventory({ page: query.page, limit: query.limit });
        return {
            rows: (response.data.data ?? []) as SupplierReturnRow[],
            count: response.data.count ?? 0,
        };
    };

    const goToReturn = (id: string) => webNavigate('/' + preLanguage + '/supplierReturn/' + id);

    const handleShowDelete = (id: string) => {
        setShowDelete(true);
        setIdDelete(id);
    };
    const handleDeleteEmployee = async () => {
        try {
            const response = await deleteSupplierReturn(idDelete);
            showDeleteToast(language?.['Supplier Return Deleted !'] ?? 'Supplier Return Deleted !');
        } catch (error) {
            showApiError(error, { language });
        } finally {
            listRef.current?.reload();
            setShowDelete(false);
        }
    };

    /* ---------------- List kit configuration ---------------- */

    const columns: ListColumn<SupplierReturnRow>[] = [
        {
            key: 'returnNumber',
            label: 'Return No.',
            width: 100,
            minWidth: 84,
            hideable: false,
            value: r => '#' + r.returnNumber,
            render: cells.primary(r => '#' + r.returnNumber),
            mobile: 'title',
            onClick: r => goToReturn(String(r.id)),
        },
        {
            key: 'supplier',
            label: 'Supplier',
            width: 150,
            minWidth: 110,
            value: r => (r.supplier ? r.supplier.companyName : '-'),
            mobile: 'subtitle',
        },
        {
            key: 'location',
            label: 'Location',
            width: 130,
            minWidth: 96,
            value: r => (r?.location ? r?.location?.name : '-'),
            mobile: 'subtitle',
        },
        {
            key: 'deliveryNumber',
            label: 'Delivery No.',
            width: 100,
            minWidth: 84,
            value: r => (r?.order ? '#' + r?.order?.internalNumber : '-'),
            mobile: 'sheet',
        },
        {
            key: 'dispatchedAt',
            label: 'Return date',
            width: 110,
            minWidth: 92,
            value: (r, ctx) => (r?.dispatchedAt ? formatUpdatedAt(r.dispatchedAt, ctx.language) : '-'),
            render: (r, ctx) => (r?.dispatchedAt ? cells.date(() => r.dispatchedAt)(r, ctx) : plain('-')),
            mobile: 'sheet',
        },
        {
            key: 'totalGross',
            label: 'Total',
            width: 96,
            minWidth: 84,
            align: 'right',
            value: r => (r?.totalGross ? '€' + r.totalGross : '-'),
            render: (r, ctx) => (r?.totalGross ? cells.money(() => r.totalGross, true)(r, ctx) : plain('-')),
            mobile: 'trailing',
        },
        {
            key: 'updatedAt',
            label: 'Last updated',
            width: 110,
            minWidth: 92,
            render: cells.date(r => r?.updatedAt),
            mobile: 'sheet',
        },
        {
            key: 'status',
            label: 'Status',
            width: 120,
            minWidth: 100,
            value: (r, ctx) => {
                const label = getStatus(r?.statusId ?? 7);
                return t(ctx.language, label, label);
            },
            render: (r, ctx) => {
                const label = getStatus(r?.statusId ?? 7);
                return <TagStatus value={label} displayLabel={ctx.language?.[label]} />;
            },
            mobile: 'trailing',
        },
    ];

    const rowActions: ListRowAction<SupplierReturnRow>[] = [
        {
            key: 'edit',
            label: 'Edit',
            icon: <EditIconV2 />,
            sheetIcon: 'inventory',
            onRun: r => goToReturn(String(r.id)),
        },
        {
            key: 'delete',
            label: 'Delete',
            icon: <DeleteIconV2 />,
            sheetIcon: 'close',
            danger: true,
            onRun: r => handleShowDelete(String(r.id)),
        },
    ];

    return (
        <>
            <div
                className="h-full relative"
                style={{
                    overflowY: isMobile ? "auto" : undefined,
                    paddingBottom: isMobile ? "24px" : undefined,
                    display: isMobile ? undefined : 'flex',
                    flexDirection: isMobile ? undefined : 'column',
                }}
            >
                <div className="lg:flex items-center justify-between">
                    <p
                        style={{ color: '#222525' }}
                        className="text-[24px] leading-[130%] font-medium font-inter"
                    >
                        {language && language?.['Supplier Return'] ? language?.['Supplier Return'] : 'Supplier Return'}
                    </p>
                </div>
                <ListScreen
                    ref={listRef}
                    language={language}
                    columns={columns}
                    fetchRows={fetchReturns}
                    search={false}
                    rowActions={rowActions}
                    countNoun="returns"
                    emptyState={inforTable}
                />
            </div>
            <div
                className={`fixed inset-0 flex items-center justify-center px-4 ${showDelete ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
                    } transition-opacity duration-300 ease-in-out`}
                style={{ backgroundColor: "#22252552", zIndex: "200", transition: "opacity 0.3s ease, visibility 0.3s ease", width: "100vw", height: "100vh", left: 0, top: 0 }}
            >
                <div
                    className={`transform transition-transform duration-300 ease-in-out ${showDelete ? 'translate-y-0' : 'translate-y-4 opacity-0'
                        }`}
                    style={{
                        padding: '24px',
                        borderRadius: '24px',
                        backgroundColor: '#FFFFFF',
                        width: '530px',
                        maxWidth: 'calc(100vw - 32px)',
                        transition: 'transform 0.3s ease, opacity 0.3s ease',
                    }}
                >
                    <div className="flex items-center justify-between">
                        <p
                            style={{ fontSize: '20px', color: '#222525' }}
                            className="font-semibold font-inter leading-[24px]"
                        >
                            {language && language?.['Delete supplier return?']
                                ? language?.['Delete supplier return?']
                                : 'Delete supplier return?'}
                        </p>
                        <div
                            onClick={() => setShowDelete(false)}
                            className="flex items-center justify-center cursor-pointer"
                            style={{
                                width: '24px',
                                height: '24px',
                                borderRadius: '100%',
                            }}
                        >
                            <CloseIcon />
                        </div>
                    </div>
                    <div className="flex items-center justify-between" style={{ marginTop: '12px' }}>
                        <p
                            className="text-[14px] font-inter leading-[24px]"
                            style={{ color: '#6A7878' }}
                        >
                            {language &&
                                language?.[
                                'This supplier return will be permanently deleted. Are you sure you want to continue'
                                ]
                                ? language?.[
                                'This supplier return will be permanently deleted. Are you sure you want to continue'
                                ]
                                : 'This supplier return will be permanently deleted. Are you sure you want to continue'}
                        </p>
                    </div>
                    <div className="flex items-center justify-end" style={{ marginTop: '24px', gap: '12px' }}>
                        <div
                            onClick={() => setShowDelete(false)}
                            className="cursor-pointer text-[14px] leading-[22px] font-inter font-medium"
                            style={{
                                borderRadius: '30px',
                                padding: '10px 24px',
                                border: '1px solid #C9D7D8',
                                color: '#222525',
                            }}
                        >
                            {language && language?.['Cancel'] ? language?.['Cancel'] : 'Cancel'}
                        </div>
                        <div
                            onClick={handleDeleteEmployee}
                            className="cursor-pointer text-[14px] leading-[22px] font-inter font-medium"
                            style={{
                                borderRadius: '30px',
                                padding: '10px 24px',
                                backgroundColor: '#BA0E13',
                                color: '#FFFFFF',
                            }}
                        >
                            {language && language?.['Delete'] ? language?.['Delete'] : 'Delete'}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default SupplierReturn;
